import classNames from 'clsx';
import React, { type MouseEvent, type ReactElement, forwardRef, memo, useCallback } from 'react';
import { useLocation } from 'react-router';
import { TABS_MAP } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.constants';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { noop } from 'reactApp/utils/helpers';

import type { ITabMenuItem } from './TabMenu.types';
import styles from './TabMenuItem.css';

const TabMenuItemComponent = (
    {
        name,
        badge,
        active,
        value,
        bottomLine,
        mod,
        color,
        isMobile,
        changeHash = true,
        onClick = noop,
        disabled,
        badgeHint = '',
        allowClickOnActive,
        blackFriday2025Enabled = false,
        marchPromo2026Enabled = false,
    }: ITabMenuItem,
    ref: React.Ref<HTMLAnchorElement>
): ReactElement => {
    const location = useLocation();

    const handleClick = useCallback(
        (event: MouseEvent) => {
            if (!changeHash) {
                event.preventDefault();
            }

            if (allowClickOnActive || !active) {
                onClick(value);
            }
        },
        [active, changeHash, onClick, value, allowClickOnActive]
    );

    return (
        <a
            data-qa-id={name}
            href={`#${value}`}
            onClick={handleClick}
            className={classNames(styles.item, {
                [styles.item_bottomLine]: bottomLine,
                [styles.item_mobile]: isMobile,
                [styles[`item_${mod}`]]: !!mod,
                [styles[`item_color_${color}`]]: !!color,
                [styles.active]: active,
                [styles.disabled]: disabled,
                [styles.action]: !location.hash || location.hash === TABS_MAP.special,
                [styles.blackFriday2025]: !active && value === 'advanced' && blackFriday2025Enabled,
                [styles.blackFriday2025Active]: blackFriday2025Enabled && active,
                [styles.marchPromo2026]: marchPromo2026Enabled,
                [styles.marchPromo2026Active]: marchPromo2026Enabled && active,
            })}
            ref={ref}
        >
            <div className={styles.name}>{name}</div>
            {badge && (
                <Hint text={badgeHint}>
                    <span className={styles.badge}>{badge}</span>
                </Hint>
            )}
        </a>
    );
};

export const TabMenuItem = memo(forwardRef(TabMenuItemComponent));

TabMenuItem.displayName = 'TabMenuItem';
